import { createAuthStore } from './authStore.svelte';
import { updateDocument } from '$lib/firebase/firestore';

const authStore = createAuthStore();

export interface TutorialStep {
  id: string;
  titleKey: string;
  contentKey: string;
  targetSelector?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  highlightPadding?: number;
}

export interface UserTutorialState {
  seenSteps: Record<string, boolean>;
  completed?: boolean;
  dismissed?: boolean;
}

// Module-level reactive state
let _activeTutorialId = $state<string | null>(null);
let _isVisible = $state(false);
let _currentStepIndex = $state(0);
let _dontShowAgain = $state(false);
let _isManualTrigger = $state(false);
let _lastAction = $state<'finish' | 'skip' | null>(null);

let _allSteps = $state<TutorialStep[]>([]);
let _currentFilteredSteps = $state<TutorialStep[]>([]);

const _currentStep = $derived(_currentFilteredSteps[_currentStepIndex] || null);
const _isLastStep = $derived(_currentFilteredSteps.length > 0 && _currentStepIndex === _currentFilteredSteps.length - 1);

function markStepSeen(tutorialId: string, stepId: string) {
  if (_isManualTrigger) return;
  const user = authStore.user as any;
  if (!user) return;

  if (!user.metadata) user.metadata = {};
  if (!user.metadata.seenTutorials) user.metadata.seenTutorials = {};
  if (!user.metadata.seenTutorials[tutorialId]) {
    user.metadata.seenTutorials[tutorialId] = { seenSteps: {} };
  }
  user.metadata.seenTutorials[tutorialId].seenSteps[stepId] = true;
}

async function saveStateToFirestore(tutorialId: string, completed: boolean, dismissed: boolean) {
  if (_isManualTrigger && !dismissed && !completed) return;
  const user = authStore.user as any;
  if (!user) return;

  if (!user.metadata) user.metadata = {};
  if (!user.metadata.seenTutorials) user.metadata.seenTutorials = {};

  const state = user.metadata.seenTutorials[tutorialId] || { seenSteps: {} };
  if (completed) state.completed = true;
  if (dismissed) state.dismissed = true;

  user.metadata.seenTutorials[tutorialId] = state;

  try {
    const plainState = JSON.parse(JSON.stringify(state));
    await updateDocument('users', {
      [`metadata.seenTutorials.${tutorialId}`]: plainState
    }, user.uid);
  } catch (e) {
    console.error(`Failed to save tutorial state for ${tutorialId}:`, e);
  }
}

function evaluateActiveStep(direction: 'next' | 'prev' = 'next') {
  if (_currentStepIndex < 0 || _currentStepIndex >= _currentFilteredSteps.length) {
    finish();
    return;
  }

  const step = _currentFilteredSteps[_currentStepIndex];
  if (step.targetSelector) {
    const el = document.querySelector(step.targetSelector);
    const isVisible = el && el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0;
    if (!isVisible) {
      console.warn(`Tutorial target not found or not visible: ${step.targetSelector}. Skipping step.`);
      if (direction === 'next') {
        _currentStepIndex++;
        evaluateActiveStep('next');
      } else {
        _currentStepIndex--;
        if (_currentStepIndex < 0) {
          _currentStepIndex = 0;
          findFirstVisibleStep();
        } else {
          evaluateActiveStep('prev');
        }
      }
      return;
    }
  }

  if (_activeTutorialId) {
    markStepSeen(_activeTutorialId, step.id);
  }
}

function findFirstVisibleStep() {
  for (let i = 0; i < _currentFilteredSteps.length; i++) {
    const step = _currentFilteredSteps[i];
    if (!step.targetSelector) {
      _currentStepIndex = i;
      if (_activeTutorialId) markStepSeen(_activeTutorialId, step.id);
      return;
    }
    const el = document.querySelector(step.targetSelector);
    const isVisible = el && el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0;
    if (isVisible) {
      _currentStepIndex = i;
      if (_activeTutorialId) markStepSeen(_activeTutorialId, step.id);
      return;
    }
  }
  closeOverlay();
}

function closeOverlay() {
  _isVisible = false;
  _activeTutorialId = null;
  _allSteps = [];
  _currentFilteredSteps = [];
  _currentStepIndex = 0;
}

function finish() {
  if (_activeTutorialId) {
    saveStateToFirestore(_activeTutorialId, true, _dontShowAgain);
  }
  _lastAction = 'finish';
  closeOverlay();
}

export const tutorialStore = {
  // Getters
  get activeTutorialId() { return _activeTutorialId; },
  get isVisible() { return _isVisible; },
  get currentStepIndex() { return _currentStepIndex; },
  get totalSteps() { return _currentFilteredSteps.length; },
  get currentStep() { return _currentStep; },
  get isLastStep() { return _isLastStep; },
  get dontShowAgain() { return _dontShowAgain; },
  set dontShowAgain(val: boolean) { _dontShowAgain = val; },
  get lastAction() { return _lastAction; },

  // Actions
  startTutorial(id: string, steps: TutorialStep[], options?: { force?: boolean }) {
    _activeTutorialId = id;
    _allSteps = steps;
    _isManualTrigger = !!options?.force;
    _dontShowAgain = false;
    _lastAction = null;

    let userSeenSteps: Record<string, boolean> = {};
    if (!_isManualTrigger) {
      const user = authStore.user as any;
      const state = user?.metadata?.seenTutorials?.[id] as UserTutorialState | undefined;

      // Don't auto-start if completed or dismissed
      if (state?.completed || state?.dismissed) {
        closeOverlay();
        return;
      }
      userSeenSteps = state?.seenSteps || {};
    }

    const stepsToFilter = _isManualTrigger
      ? steps
      : steps.filter(s => !userSeenSteps[s.id]);

    if (stepsToFilter.length === 0) {
      closeOverlay();
      return;
    }

    _currentFilteredSteps = stepsToFilter;
    _currentStepIndex = 0;
    _isVisible = true;

    evaluateActiveStep('next');
  },

  nextStep() {
    if (_currentStepIndex < _currentFilteredSteps.length - 1) {
      _currentStepIndex++;
      evaluateActiveStep('next');
    } else {
      finish();
    }
  },

  prevStep() {
    if (_currentStepIndex > 0) {
      _currentStepIndex--;
      evaluateActiveStep('prev');
    }
  },

  skip() {
    if (_activeTutorialId) {
      saveStateToFirestore(_activeTutorialId, false, _dontShowAgain);
    }
    _lastAction = 'skip';
    closeOverlay();
  },

  finish,

  async resetTutorial(tutorialId: string) {
    const user = authStore.user as any;
    if (!user) return;

    if (user.metadata?.seenTutorials?.[tutorialId]) {
      delete user.metadata.seenTutorials[tutorialId];
    }

    try {
      const { deleteField } = await import('firebase/firestore');
      await updateDocument('users', {
        [`metadata.seenTutorials.${tutorialId}`]: deleteField()
      }, user.uid);
    } catch (e) {
      console.error(`Failed to reset tutorial ${tutorialId}:`, e);
    }
  },

  async resetAllTutorials() {
    const user = authStore.user as any;
    if (!user) return;

    if (user.metadata) {
      user.metadata.seenTutorials = {};
    }

    try {
      await updateDocument('users', {
        'metadata.seenTutorials': {}
      }, user.uid);
    } catch (e) {
      console.error('Failed to reset all tutorials:', e);
    }
  },

  // Forces position recalculation trigger (e.g. on scroll)
  triggerPositionUpdate() {
    // We re-assign to trigger derived values or effects
    _currentStepIndex = _currentStepIndex;
  }
};
